import {
  GetLimitTagsToRenderParams,
  ReOrderOptionsParams,
  getVisibleTagsCountParams,
  handleChangeAutocompleteInputParams,
} from '../types/autocomplete.utils.types';

const getVisibleTagsCount = <T>({
  values,
  maxAvailableWidth,
  getOptionLabel,
}: getVisibleTagsCountParams<T>) => {
  let visibleTagsCount = 0;
  let usedWidth = 0;
  for (const item of values) {
    const CHIP_MARGIN_RIGHT = 18;
    const CHIP_TEXT_PADDING = 16;
    const DELETE_ICON_WIDTH = 16;
    const DELETE_ICON_MARGIN = 8;
    const AVERAGE_CHAR_WIDTH = 9.5;
    const labelWidth = getOptionLabel(item as T).length * AVERAGE_CHAR_WIDTH;
    const chipWidth =
      labelWidth + DELETE_ICON_WIDTH + CHIP_MARGIN_RIGHT + CHIP_TEXT_PADDING + DELETE_ICON_MARGIN;

    if (usedWidth + chipWidth > maxAvailableWidth) {
      break;
    }

    usedWidth += chipWidth;
    visibleTagsCount++;
  }

  return visibleTagsCount;
};

export const getLimitTagsToRender = <T>({
  autocompleteWidth,
  isInputOpen,
  value,
  getOptionLabel,
}: GetLimitTagsToRenderParams<T>): number => {
  if (!autocompleteWidth) {
    return 0;
  }
  if (isInputOpen) {
    return -1;
  }

  const AUTOCOMPLETE_INPUT_PADDING = 71;
  const AVERAGE_CHAR_WIDTH = 9.5;
  const QUANTITY_OF_INVISIBLE_TAGS_INDICATOR_TEXT_WIDTH = '+1'.length * AVERAGE_CHAR_WIDTH;

  const maxAvailableWidth =
    autocompleteWidth -
    AUTOCOMPLETE_INPUT_PADDING -
    QUANTITY_OF_INVISIBLE_TAGS_INDICATOR_TEXT_WIDTH;

  const values = Array.isArray(value) ? value : [value];
  const visibleTagsCount = getVisibleTagsCount<T>({
    values: values as T[],
    maxAvailableWidth,
    getOptionLabel,
  });
  return visibleTagsCount;
};

export const reOrderOptions = <T>({ options, selectedItems }: ReOrderOptionsParams<T>): T[] => {
  const selectedSet = new Set(selectedItems);
  const selected: T[] = [];
  const unselected: T[] = [];

  for (const option of options) {
    if (selectedSet.has(option)) {
      selected.push(option);
    } else {
      unselected.push(option);
    }
  }

  return [...selected, ...unselected];
};

export const handleChangeAutocompleteInput = ({
  inputParams: { evt, value, reason },
  checkbox,
  multiple,
  debouncedInputChange,
  onChangeInputText,
  onInputChange,
  onClear,
}: handleChangeAutocompleteInputParams) => {
  const isReset = reason === 'reset';
  const isEmptyReset = !multiple && isReset && value === '' && evt?.type !== 'blur';

  const shouldChangeText = (!checkbox || !isReset) && !isEmptyReset;

  if (shouldChangeText) {
    onChangeInputText(value);
  }

  if (onClear && reason === 'clear') {
    onChangeInputText('');
    onClear();
  }

  if (reason === 'input') {
    debouncedInputChange(value);
  }

  if (onInputChange) {
    if (reason !== 'reset') {
      onInputChange(evt, value, reason);
    }
  }
};
